// useDiaryDrivers — fetches the active drivers feeding the diary selector.
//
// Cached for 15 minutes (matches the server's drivers cache TTL); no
// refetch-on-focus because the driver roster rarely changes mid-session.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useAuthStore } from '../../../store/authStore';
import type { DriverListResponse, DriverSummary } from '../../../types/api';

const STALE_MS = 15 * 60_000;

export function useDiaryDrivers() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery<DriverSummary[]>({
    queryKey: queryKeys.diary.drivers,
    queryFn: async () => {
      const res = await apiClient.get<DriverListResponse>(endpoints.drivers.list, {
        params: { status: 'active', limit: 100 },
      });
      const drivers = [...res.data.drivers];
      drivers.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      return drivers;
    },
    enabled: isAuthenticated,
    staleTime: STALE_MS,
    gcTime: STALE_MS,
    refetchOnWindowFocus: false,
  });
}
